import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../../../components/Loader/Loader.jsx";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Offcanvas } from "react-bootstrap";
import LiteTradeSelectAccount
    from "../../../../modules/LiteTrade/components/LiteTradeSelectAccount/LiteTradeSelectAccount.jsx";
import _ from "lodash";
import SideSelector from "../../../../components/SideSelector/SideSelector.jsx";
import { MobileMenuFixedStyled, MobileMenuStyled } from "../../styled/MobileMenuStyled.js";
import {
    MobilePositionCreateAmountPresetsStyled,
    MobilePositionCreateChartStyled,
    MobilePositionCreateInsufficientFundsCloseStyled,
    MobilePositionCreateInsufficientFundsStyled,
    MobilePositionCreatePresetsStyled,
    MobilePositionCreateStyled
} from "./styled/MobilePositionCreateStyled.js";
import {
    addMarketDataWatchAction,
    openLiteTradePositionAction,
    removeMarketDataWatchAction
} from "../../../../modules/LiteTrade/liteTradeActions.js";
import OrderTypeSelector from "../../../../components/OrderTypeSelector/OrderTypeSelector.jsx";
import { ORDER_TYPES } from "../../../../components/OrderTypeSelector/orderTypeSelectorConstants.js";
import { amountPercentPresets } from "./mobilePositionCreateConstants.js";
import { BinIcon } from "../../../../components/icon/Icon.jsx";
import { LITE_TRADE_POSITION_SIDE } from "../../../../modules/LiteTrade/liteTradeConstants.js";
import {
    getLiteTradeAccountMetrics,
    getLiteTradeAccountPortfolio,
    makeSelectLiteTradeMarketDataBySymbol,
    makeSelectorGetLiteTradeAssetInfo
} from "../../../../modules/LiteTrade/liteTradeSelectors.js";
import {
    getAppUser,
    getIsUserSelectedAccountTypeLive,
    getUserSelectedAccountCode
} from "../../../../app/appSelectors.js";
import withDebounced from "../../../../hooks/withDebounced.js";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";
import MobilePositionAmountEditor from "../MobilePositionAmountEditor/MobilePositionAmountEditor";
import MobilePositionParamSwitch from "../MobilePositionParamSwitch/MobilePositionParamSwitch";
import MobilePositionParamEditor from "../MobilePositionParamEditor/MobilePositionParamEditor";
import { MobilePositionUpdateDividerStyled } from "../MobilePositionUpdate/styled/MobilePositionUpdateStyled";
import { useHideKeyboardOnScroll } from "../../../mobileAppHooks";
import useAccountStorage from "../../../../hooks/useAccountStorage.js";
import parseAccountName from "../../../../utils/parseAccountName.js";
import LiteTradeChartTickerWrapper
    from "../../../../modules/LiteTrade/components/LiteTradeChart/LiteTradeChartTickerWrapper.jsx";
import { useMobilePositionFormErrors } from "../mobilePositionHooks";
import { useAssetInstance } from "../../../../modules/Asset/assetHooks";
import MobilePositionCreateTopUpBanner from "./MobilePositionCreateTopUpBanner";
import { openMobileSliderPageAction } from "../../../mobileAppActions";
import { MOBILE_SLIDER_PAGES_IDS } from "../../MobileSliderPages/mobileSliderPagesConstants";

function MobilePositionCreate(props) {
    const {
        side: initSide,
        symbol,
    } = props;

    const dispatch = useDispatch();
    const assetInfo = useSelector(makeSelectorGetLiteTradeAssetInfo(symbol));
    const marketDataSymbol = useSelector(makeSelectLiteTradeMarketDataBySymbol(symbol));
    const metrics = useSelector(getLiteTradeAccountMetrics);
    const selectedAccountCode = useSelector(getUserSelectedAccountCode);
    const portfolio = useSelector(getLiteTradeAccountPortfolio);
    const user = useSelector(getAppUser);
    const isSelectedAccountTypeLive = useSelector(getIsUserSelectedAccountTypeLive);

    const {getLangPath} = useLangContext();
    const {getAccountStorageItem, setAccountStorageItem} = useAccountStorage();
    const [lots, setLots] = useState('0');
    const [amount, setAmount] = useState('0');
    const [side, setSide] = useState(initSide);
    const [orderType, setOrderType] = useState('');
    const [stopLossActive, setStopLossActive] = useState(false);
    const [takeProfitActive, setTakeProfitActive] = useState(false);
    const [additionalFields, setAdditionalFields] = useState({});
    const [inited, setInited] = useState(false);
    const [percent, setPercent] = useState(null);
    const {isAssetInstanceReady, assetInstance} = useAssetInstance(symbol);
    const [creatingPosition, setCreatingPosition] = useState(false);
    const [showInsufficientFunds, setShowInsufficientFunds] = useState(false);

    const buyLabel = getLangPath('lite_version.order.buy');
    const sellLabel = getLangPath('lite_version.order.sell');

    useHideKeyboardOnScroll();

    const filledQuantity = useMemo(() => {
        const result = {
            [LITE_TRADE_POSITION_SIDE.BUY]: 0,
            [LITE_TRADE_POSITION_SIDE.SELL]: 0,
        };
        portfolio.positions?.forEach(item => {
            if (item.symbol === symbol) {
                result[item.side] += item.quantity;
            }
        });
        return result;
    }, [portfolio, symbol]);

    useEffect(() => {
        if (assetInfo) {
            dispatch(addMarketDataWatchAction({fromComponentName: 'create_order', symbol: assetInfo.symbol}));
        }
        return () => {
            dispatch(removeMarketDataWatchAction({fromComponentName: 'create_order'}));
        }
    }, [assetInfo, dispatch]);

    const isBuy = useMemo(() => (side === LITE_TRADE_POSITION_SIDE.BUY), [side]);

    const additionalFieldChange = useCallback((field, value) => {
        setAdditionalFields(prevState => ({
            ...prevState,
            [field]: value,
        }));
    }, []);

    const limitPriceChange = useCallback((value) => additionalFieldChange('limitPrice', value), [additionalFieldChange]);
    const stopPriceChange = useCallback((value) => additionalFieldChange('stopPrice', value), [additionalFieldChange]);
    const stopLossChange = useCallback((value) => additionalFieldChange('stopLoss', value), [additionalFieldChange]);
    const takeProfitChange = useCallback((value) => additionalFieldChange('takeProfit', value), [additionalFieldChange]);

    const getCurrentPrice = useCallback(() => {
        if (marketDataSymbol?.symbol) {
            return marketDataSymbol[isBuy ? 'ask' : 'bid'];
        }
    }, [marketDataSymbol, isBuy]);

    const lotsChangeHandler = useCallback((value, manual = true) => {
        setLots(value);
        if (manual) {
            setAccountStorageItem('inputLastLots', value);
            setAccountStorageItem('inputLastMethod', 'lots');
        }

        const amount = assetInstance.lotsToAmount(parseFloat(value) || 0, getCurrentPrice(), side, filledQuantity);
        setAmount(amount.toFixed(2));
    }, [assetInstance, getCurrentPrice, side, filledQuantity, setAccountStorageItem]);

    const amountChangeHandler = useCallback((value, manual = true) => {
        setAmount(value);
        if (manual) {
            const percent = parseFloat(value) / parseFloat(metrics['marginFree']) * 100;
            setAccountStorageItem('inputLastPercent', percent);
            setAccountStorageItem('inputLastMethod', 'percent');
        }

        const lots = assetInstance.amountToLots(value, getCurrentPrice(), side, filledQuantity)
        setLots(assetInstance.roundLots(lots));
    }, [assetInstance, filledQuantity, getCurrentPrice, metrics, setAccountStorageItem, side]);

    const submitClickHandler = useCallback(() => {
        setCreatingPosition(true);
        dispatch(openLiteTradePositionAction({
            orderType,
            symbol,
            quantity: lots * assetInstance.getLotSize(),
            side,
            stopLossActive,
            takeProfitActive,
            ...additionalFields,
        }));
    }, [dispatch, orderType, symbol, lots, assetInstance, side, stopLossActive, takeProfitActive, additionalFields]);

    /**
     * Presets click handler
     */
    const setPercentHandler = useCallback((percent) => {
        percent = parseFloat(percent);
        setPercent(percent);
        const minOrderSizeAmount = assetInstance.getMinOrderSizeAmount(getCurrentPrice());
        amountChangeHandler(_.max([_.floor(metrics['marginFree'] * percent / 100), minOrderSizeAmount]).toFixed(2), false);
    }, [assetInstance, getCurrentPrice, amountChangeHandler, metrics]);

    const presetPercentItemClickHandler = useCallback((e) => {
        const percent = e.currentTarget.dataset.value;
        setAccountStorageItem('inputLastPercent', percent);
        setAccountStorageItem('inputLastMethod', 'percent');
        setPercentHandler(percent);
    }, [setAccountStorageItem, setPercentHandler]);

    const getPresetPercentItem = useCallback((preset) => {
        const minOrderSizeAmount = assetInstance.getMinOrderSizeAmount(getCurrentPrice());
        const isDisabled = _.floor(metrics['marginFree'] * preset.value / 100) < minOrderSizeAmount;
        return <Button variant={'preset'}
                       key={preset.value}
                       active={preset.value === percent}
                       disabled={isDisabled}
                       data-value={preset.value}
                       onClick={presetPercentItemClickHandler}
        >{preset.icon ? <BinIcon name={preset.icon}/> : preset.title}</Button>
    }, [assetInstance, getCurrentPrice, metrics, percent, presetPercentItemClickHandler]);

    const getDefaultTakeProfit = useCallback(() => {
        if (!marketDataSymbol) return;
        const spread = marketDataSymbol.ask - marketDataSymbol.bid;
        const price = (orderType === ORDER_TYPES.MARKET) ? getCurrentPrice()
            : (orderType === ORDER_TYPES.STOP) ? additionalFields['stopPrice'] : additionalFields['limitPrice'];
        return isBuy ? (price + 20 * spread) : (price - 20 * spread);
    }, [marketDataSymbol, orderType, getCurrentPrice, additionalFields, isBuy]);

    const getDefaultStopLoss = useCallback(() => {
        if (!marketDataSymbol) return;
        const spread = marketDataSymbol.ask - marketDataSymbol.bid;
        const price = (orderType === ORDER_TYPES.MARKET) ? getCurrentPrice()
            : (orderType === ORDER_TYPES.STOP) ? additionalFields['stopPrice'] : additionalFields['limitPrice'];
        return isBuy ? (price - 20 * spread) : (price + 20 * spread);
    }, [marketDataSymbol, orderType, getCurrentPrice, additionalFields, isBuy]);

    const getDefaultLimitPrice = useCallback(() => {
        if (!marketDataSymbol) return;
        const spread = marketDataSymbol.ask - marketDataSymbol.bid;
        return isBuy ? (marketDataSymbol.bid - 20 * spread) : (marketDataSymbol.ask + 20 * spread);
    }, [marketDataSymbol, isBuy]);

    const getDefaultStopPrice = useCallback(() => {
        if (!marketDataSymbol) return;
        const spread = marketDataSymbol.ask - marketDataSymbol.bid;
        return isBuy ? (marketDataSymbol.bid + 20 * spread) : (marketDataSymbol.ask - 20 * spread);
    }, [marketDataSymbol, isBuy]);

    const formErrors = useMobilePositionFormErrors({
        additionalFields,
        amount,
        assetInstance,
        currentPrice: getCurrentPrice(),
        lots,
        metrics,
        orderType,
        side,
        stopLossActive,
        takeProfitActive,
    });

    const isFormErrorsEmpty = useMemo(() => Object.values(formErrors).length === 0, [formErrors]);

    const stopLossActiveChangeHandler = useCallback(() => {
        setStopLossActive(prevState => !prevState);
        additionalFieldChange('stopLoss', getDefaultStopLoss());
    }, [additionalFieldChange, getDefaultStopLoss]);

    const takeProfitActiveChangeHandler = useCallback(() => {
        setTakeProfitActive(prevState => !prevState);
        additionalFieldChange('takeProfit', getDefaultTakeProfit());
    }, [additionalFieldChange, getDefaultTakeProfit]);

    const setOrderTypeHandler = useCallback((type) => {
        if (type === ORDER_TYPES.LIMIT) {
            additionalFieldChange('limitPrice', getDefaultLimitPrice());
            setTakeProfitActive(false);
            setStopLossActive(false);
        }
        if (type === ORDER_TYPES.STOP) {
            additionalFieldChange('stopPrice', getDefaultStopPrice());
            setTakeProfitActive(false);
            setStopLossActive(false);
        }
        setOrderType(type);
    }, [additionalFieldChange, getDefaultLimitPrice, getDefaultStopPrice]);

    const setSideHandler = useCallback((side) => {
        setSide(side);

        if (lots) {
            const amount = assetInstance.lotsToAmount(lots, getCurrentPrice(), side, filledQuantity);
            setAmount(amount.toFixed(2));
        }
    }, [assetInstance, filledQuantity, getCurrentPrice, lots]);

    /**
     * INIT
     */
    useEffect(() => {
        if (!inited && assetInstance && isAssetInstanceReady && marketDataSymbol && parseAccountName(metrics?.account)?.accountCode === selectedAccountCode) {
            const inputLastMethod = getAccountStorageItem('inputLastMethod');
            if (inputLastMethod === 'percent') {
                const lastPercent = getAccountStorageItem('inputLastPercent');
                setPercentHandler(lastPercent);
            } else {
                const lastLost = getAccountStorageItem('inputLastLots');
                lotsChangeHandler(lastLost || assetInstance.getMinOrderSizeLots(), false);
            }
            setInited(true);
        }
    }, [assetInstance, getAccountStorageItem, inited, isAssetInstanceReady, lotsChangeHandler, marketDataSymbol, metrics?.account, selectedAccountCode, setPercentHandler]);

    useEffect(() => {
        if (selectedAccountCode) {
            setInited(false);
        }
    }, [selectedAccountCode]);

    const handleCloseInsufficientFunds = useCallback(() => {
        setShowInsufficientFunds(false);
    }, []);

    const handleSubmitButtonWrapperClick = useCallback(() => {
        if (formErrors['lots'] === 'error.margin_free_not_enough' && isSelectedAccountTypeLive) {
            setShowInsufficientFunds(true);
        }
    }, [formErrors, isSelectedAccountTypeLive]);

    const handleTopUpButtonClick = useCallback(() => {
        dispatch(openMobileSliderPageAction(MOBILE_SLIDER_PAGES_IDS.DEPOSIT));
        handleCloseInsufficientFunds();
    }, [dispatch, handleCloseInsufficientFunds]);

    const currentPriceKey = `lite_version.order.${isBuy ? "ask_price" : "bid_price"}`;
    const currentPriceLabel = getLangPath(`lite_version.order.${currentPriceKey}`);
    const protectionOrderComparePriceLabel = useMemo(() => {
        const key = orderType === ORDER_TYPES.LIMIT
            ? 'lite_version.order.types.limit.price'
            : orderType === ORDER_TYPES.STOP
                ? 'lite_version.order.types.stop.price'
                : currentPriceKey;
        return getLangPath(key)
    }, [currentPriceKey, getLangPath, orderType]);

    if (!assetInfo || !assetInstance || !marketDataSymbol) {
        return <Loader show={true}/>
    }

    return <>
        {
            !!creatingPosition && <Loader show={true}/>
        }
        <FormGroup className={'mb-3'}>
            <LiteTradeSelectAccount metricParam={'marginFree'} showCurrentValue={true}/>
        </FormGroup>
        {
            (!user?.buyer && isSelectedAccountTypeLive)
                ? <MobilePositionCreateTopUpBanner/>
                : <>
                    <FormGroup className={'mb-3'}>
                        <MobilePositionAmountEditor
                            amount={amount}
                            amountChangeHandler={amountChangeHandler}
                            assetInstance={assetInstance}
                            currentPrice={getCurrentPrice()}
                            lotsChangeHandler={lotsChangeHandler}
                            lots={lots}
                            maxLots={assetInstance.amountToLots(metrics['marginFree'], getCurrentPrice())}
                            error={formErrors['lots']}
                        />
                        <MobilePositionCreatePresetsStyled>
                            <MobilePositionCreateAmountPresetsStyled>
                                {amountPercentPresets.map(getPresetPercentItem)}
                            </MobilePositionCreateAmountPresetsStyled>
                        </MobilePositionCreatePresetsStyled>
                    </FormGroup>

                    <FormGroup className={'mb-3'}>
                        <OrderTypeSelector onChange={setOrderTypeHandler}/>
                    </FormGroup>

                    {
                        orderType === ORDER_TYPES.LIMIT && <>
                            <MobilePositionParamEditor
                                label={getLangPath('lite_version.order.types.limit.price')}
                                inputPriceLabel={getLangPath('lite_version.order.types.limit.price')}
                                comparePriceLabel={currentPriceLabel}
                                min={0}
                                onChange={limitPriceChange}
                                priceIncrement={assetInfo.info.priceIncrement}
                                value={additionalFields['limitPrice']}
                                error={formErrors['limitPrice']}
                            />
                            <MobilePositionUpdateDividerStyled/>
                        </>
                    }

                    {
                        orderType === ORDER_TYPES.STOP && <>
                            <MobilePositionParamEditor
                                label={getLangPath('lite_version.order.types.stop.price')}
                                inputPriceLabel={getLangPath('lite_version.order.types.stop.price')}
                                comparePriceLabel={currentPriceLabel}
                                min={0}
                                onChange={stopPriceChange}
                                priceIncrement={assetInfo.info.priceIncrement}
                                value={additionalFields['stopPrice']}
                                error={formErrors['stopPrice']}
                            />
                            <MobilePositionUpdateDividerStyled/>
                        </>
                    }

                    <FormGroup className={'my-3'}>
                        <SideSelector side={side} onChange={setSideHandler}/>
                    </FormGroup>

                    <MobilePositionParamSwitch
                        label={getLangPath('lite_version.order.stop_loss')}
                        checked={stopLossActive}
                        onChange={stopLossActiveChangeHandler}
                    />
                    {
                        !!stopLossActive && <>
                            <MobilePositionParamEditor
                                priceIncrement={assetInfo.info.priceIncrement}
                                label={getLangPath('lite_version.order.price')}
                                inputPriceLabel={getLangPath('lite_version.order.stop_loss')}
                                comparePriceLabel={protectionOrderComparePriceLabel}
                                min={0}
                                value={additionalFields['stopLoss']}
                                onChange={stopLossChange}
                                error={formErrors['stopLoss']}
                            />
                            <MobilePositionUpdateDividerStyled/>
                        </>
                    }

                    <MobilePositionParamSwitch
                        label={getLangPath('lite_version.order.take_profit')}
                        checked={takeProfitActive}
                        onChange={takeProfitActiveChangeHandler}
                    />
                    {
                        !!takeProfitActive && <>
                            <MobilePositionParamEditor
                                priceIncrement={assetInfo.info.priceIncrement}
                                label={getLangPath('lite_version.order.price')}
                                inputPriceLabel={getLangPath('lite_version.order.take_profit')}
                                comparePriceLabel={protectionOrderComparePriceLabel}
                                min={0}
                                value={additionalFields['takeProfit']}
                                onChange={takeProfitChange}
                                error={formErrors['takeProfit']}
                            />
                            <MobilePositionUpdateDividerStyled/>
                        </>
                    }

                    <MobilePositionCreateChartStyled>
                        <LiteTradeChartTickerWrapper symbol={assetInfo.symbol}
                                                     decimal={-Math.log10(assetInstance.getPriceIncrement())}
                                                     onlyOneChartLine={isBuy ? 'ask' : 'bid'}
                        />

                    </MobilePositionCreateChartStyled>

                    <FormGroup className={'pb-5'}>&nbsp;</FormGroup>

                    <MobileMenuFixedStyled>
                        <MobileMenuStyled>
                            {/*onClick to catch click on disabled button*/}
                            <MobilePositionCreateStyled onClick={handleSubmitButtonWrapperClick}>
                                <Button variant={'primary'}
                                        size={'lg'}
                                        disabled={!isFormErrorsEmpty}
                                        onClick={submitClickHandler}>{isBuy ? buyLabel : sellLabel} {symbol}</Button>
                            </MobilePositionCreateStyled>
                        </MobileMenuStyled>
                    </MobileMenuFixedStyled>
                </>
        }
        <Offcanvas
            show={showInsufficientFunds}
            placement={'bottom'}
            onHide={handleCloseInsufficientFunds}         
        >
            <MobilePositionCreateInsufficientFundsStyled>
                <MobilePositionCreateInsufficientFundsCloseStyled onClick={handleCloseInsufficientFunds}>
                    <BinIcon name={'IcClose'}/>
                </MobilePositionCreateInsufficientFundsCloseStyled>
                <h3>{getLangPath('deposit.insufficient_funds')}</h3>
                <p>{getLangPath('deposit.top_up_or_close_positions')}</p>
                <Button variant={'primary'} size={'lg'} onClick={handleTopUpButtonClick}>
                    {getLangPath('lite_version.home.top_up')}
                </Button>
            </MobilePositionCreateInsufficientFundsStyled>
        </Offcanvas>
    </>
}

export default withDebounced({
    debounce: 1000,
    propNames: ['marketDataSymbol', 'metrics'],
})(memo(MobilePositionCreate));
